import {
    ArrowBack,
    CheckCircle,
    QrCode2,
    Refresh,
    Send,
    WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { API_URL } from '../config/api';

function WhatsAppPanel({ token }) {
    const [qr, setQr] = useState(null);
    const [ready, setReady] = useState(false);
    const [state, setState] = useState('NOT_INITIALIZED');
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openSendDialog, setOpenSendDialog] = useState(false);
    const [sendData, setSendData] = useState({ phone: '', message: '' });
    const [sendStatus, setSendStatus] = useState(null);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [replyMessage, setReplyMessage] = useState('');
    const [sendingReply, setSendingReply] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [sendingMedia, setSendingMedia] = useState(false);
    const [mediaCaption, setMediaCaption] = useState('');
    const [mediaUrls, setMediaUrls] = useState({});
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const mediaUrlsRef = useRef({});

    const apiFetch = (url, options = {}) => fetch(url, {
        ...options,
        headers: {
            ...(options.headers || {}),
            Authorization: `Bearer ${token}`
        }
    });

    const clearMediaUrls = () => {
        Object.values(mediaUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
        mediaUrlsRef.current = {};
        setMediaUrls({});
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchStatus = async () => {
        try {
            const statusRes = await apiFetch(`${API_URL}/api/whatsapp/status`);
            const statusData = await statusRes.json();
            setReady(statusData.ready);
            setState(statusData.state || 'UNKNOWN');

            if (!statusData.ready) {
                const qrRes = await apiFetch(`${API_URL}/api/whatsapp/qr`);
                const qrData = await qrRes.json();
                setQr(qrData.qr);
            } else {
                setQr(null);
            }
        } catch (error) {
            console.error('Error fetching status:', error);
            setState('ERROR');
        }
    };

    const fetchChats = async () => {
        if (!ready) return;

        setLoading(true);
        try {
            const response = await apiFetch(`${API_URL}/api/whatsapp/chats`);
            const data = await response.json();

            if (data.success) {
                setChats(data.data);
            }
        } catch (error) {
            console.error('Error fetching chats:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (chatId, silent = false) => {
        if (!silent) {
            setLoadingMessages(true);
        }
        try {
            const response = await apiFetch(`${API_URL}/api/whatsapp/chat/${encodeURIComponent(chatId)}/messages`);
            const data = await response.json();

            if (data.success) {
                setMessages(data.data);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            if (!silent) {
                setLoadingMessages(false);
            }
        }
    };

    const handleChatClick = (chat) => {
        clearMediaUrls();
        setSelectedChat(chat);
        fetchMessages(chat.id);
    };

    const handleBackToChats = () => {
        clearMediaUrls();
        setSelectedChat(null);
        setMessages([]);
        setReplyMessage('');
    };

    const handleImageClick = (imageUrl) => {
        setImagePreview(imageUrl);
        setOpenImageDialog(true);
    };

    const handleCloseImageDialog = () => {
        setOpenImageDialog(false);
        setImagePreview(null);
    };

    const handleSendReply = async () => {
        if (!replyMessage.trim() || !selectedChat) return;

        setSendingReply(true);
        try {
            // Извлекаем номер телефона из chatId
            const phone = selectedChat.id.split('@')[0];

            const response = await apiFetch(`${API_URL}/api/whatsapp/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    phone: phone,
                    message: replyMessage
                }),
            });

            const data = await response.json();

            if (data.success) {
                setReplyMessage('');
                // Обновляем сообщения
                setTimeout(() => fetchMessages(selectedChat.id), 1000);
            } else {
                alert('Ошибка отправки: ' + data.error);
            }
        } catch (error) {
            alert('Ошибка: ' + error.message);
        } finally {
            setSendingReply(false);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);

            // Создаем превью для изображений
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFilePreview(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setFilePreview(null);
            }
        }
    };

    const handleSendMedia = async () => {
        if (!selectedFile || !selectedChat) return;

        setSendingMedia(true);
        try {
            const phone = selectedChat.id.split('@')[0];

            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('phone', phone);
            formData.append('caption', mediaCaption);

            const response = await apiFetch(`${API_URL}/api/whatsapp/send-media`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (data.success) {
                setSelectedFile(null);
                setFilePreview(null);
                setMediaCaption('');
                // Обновляем сообщения
                setTimeout(() => fetchMessages(selectedChat.id), 1000);
            } else {
                alert('Ошибка отправки: ' + data.error);
            }
        } catch (error) {
            alert('Ошибка: ' + error.message);
        } finally {
            setSendingMedia(false);
        }
    };

    const handleSendMessage = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/whatsapp/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(sendData),
            });

            const data = await response.json();

            if (data.success) {
                setSendStatus({ type: 'success', message: 'Сообщение отправлено!' });
                setSendData({ phone: '', message: '' });
                setTimeout(() => {
                    setOpenSendDialog(false);
                    setSendStatus(null);
                }, 2000);
            } else {
                setSendStatus({ type: 'error', message: data.error });
            }
        } catch (error) {
            setSendStatus({ type: 'error', message: error.message });
        }
    };

    useEffect(() => {
        fetchStatus();
        const interval = setInterval(fetchStatus, 5000); // Проверяем статус каждые 5 секунд
        return () => clearInterval(interval);
    }, []);

    useEffect(() => () => {
        clearMediaUrls();
    }, []);

    useEffect(() => {
        if (ready) {
            fetchChats();
            // Автообновление списка чатов каждые 10 секунд
            const chatsInterval = setInterval(fetchChats, 10000);
            return () => clearInterval(chatsInterval);
        }
    }, [ready]);

    // Автообновление сообщений когда открыт чат
    useEffect(() => {
        if (selectedChat && ready) {
            const interval = setInterval(() => {
                fetchMessages(selectedChat.id, true); // silent = true - без индикатора загрузки
            }, 3000); // Обновляем сообщения каждые 3 секунды

            return () => clearInterval(interval);
        }
    }, [selectedChat, ready]);

    // Автоматическая прокрутка при изменении сообщений
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (!selectedChat || messages.length === 0) {
            return;
        }

        let cancelled = false;

        const loadMediaUrls = async () => {
            for (const msg of messages) {
                if (!msg.hasMedia || mediaUrlsRef.current[msg.id]) {
                    continue;
                }

                try {
                    const response = await apiFetch(`${API_URL}/api/whatsapp/media/${encodeURIComponent(msg.chatId)}/${msg.id}`);
                    if (!response.ok) {
                        continue;
                    }

                    const blob = await response.blob();
                    if (cancelled) {
                        return;
                    }

                    const objectUrl = URL.createObjectURL(blob);
                    mediaUrlsRef.current[msg.id] = objectUrl;
                    setMediaUrls((prev) => ({ ...prev, [msg.id]: objectUrl }));
                } catch (error) {
                    console.error('Error loading media blob:', error);
                }
            }
        };

        loadMediaUrls();

        return () => {
            cancelled = true;
        };
    }, [messages, selectedChat, token]);

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            {selectedChat ? (
                // Просмотр сообщений конкретного чата
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <IconButton onClick={handleBackToChats}>
                            <ArrowBack />
                        </IconButton>
                        <Avatar sx={{ bgcolor: '#25d366' }}>
                            <WhatsAppIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="h5">{selectedChat.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {selectedChat.isGroup ? 'Группа' : 'Личный чат'}
                            </Typography>
                        </Box>
                    </Box>

                    <Card sx={{ height: 'calc(100vh - 250px)', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                            {loadingMessages ? (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                    Загрузка сообщений...
                                </Typography>
                            ) : messages.length === 0 ? (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                    Нет сообщений
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {messages.map((msg, index) => (
                                        <Paper
                                            key={msg.id}
                                            sx={{
                                                animation: 'fadeIn 0.3s ease-in',
                                                '@keyframes fadeIn': {
                                                    from: { opacity: 0, transform: 'translateY(10px)' },
                                                    to: { opacity: 1, transform: 'translateY(0)' }
                                                },
                                                p: 2,
                                                maxWidth: '70%',
                                                alignSelf: msg.fromMe ? 'flex-end' : 'flex-start',
                                                bgcolor: msg.fromMe ? '#dcf8c6' : '#fff',
                                                borderRadius: 2
                                            }}
                                        >
                                            {!msg.fromMe && (
                                                <Typography variant="caption" color="primary" fontWeight="bold">
                                                    {msg.from}
                                                </Typography>
                                            )}
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    wordBreak: 'break-word',
                                                    fontStyle: msg.mediaType ? 'italic' : 'normal',
                                                    color: msg.mediaType ? 'text.secondary' : 'text.primary'
                                                }}
                                            >
                                                {msg.message}
                                            </Typography>

                                            {/* Отображение медиа */}
                                            {msg.hasMedia && msg.mediaType === 'image' && (
                                                <Box sx={{ mt: 1 }}>
                                                    {mediaUrls[msg.id] ? (
                                                        <img
                                                            src={mediaUrls[msg.id]}
                                                            alt="Изображение"
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: '300px',
                                                                borderRadius: '8px',
                                                                cursor: 'pointer',
                                                                transition: 'transform 0.2s'
                                                            }}
                                                            onMouseOver={(e) => e.target.style.transform = 'scale(1.02)'}
                                                            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                                            onClick={() => handleImageClick(mediaUrls[msg.id])}
                                                        />
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Загрузка изображения...
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}

                                            {msg.hasMedia && msg.mediaType === 'video' && (
                                                <Box sx={{ mt: 1 }}>
                                                    {mediaUrls[msg.id] ? (
                                                        <video
                                                            controls
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: '300px',
                                                                borderRadius: '8px'
                                                            }}
                                                        >
                                                            <source src={mediaUrls[msg.id]} />
                                                        </video>
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Загрузка видео...
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}

                                            {msg.hasMedia && (msg.mediaType === 'audio' || msg.mediaType === 'document') && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ mt: 1 }}
                                                    disabled={!mediaUrls[msg.id]}
                                                    onClick={() => window.open(mediaUrls[msg.id], '_blank')}
                                                >
                                                    Скачать {msg.mediaType === 'audio' ? 'аудио' : 'файл'}
                                                </Button>
                                            )}

                                            {msg.mediaType && (
                                                <Chip
                                                    label={msg.mediaType.toUpperCase()}
                                                    size="small"
                                                    color="info"
                                                    sx={{ mt: 1 }}
                                                />
                                            )}
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {formatTimestamp(msg.timestamp)}
                                            </Typography>
                                        </Paper>
                                    ))}
                                    {/* Невидимый элемент для прокрутки */}
                                    <div ref={messagesEndRef} />
                                </Box>
                            )}
                        </CardContent>

                        {/* Поле для ответа */}
                        <Divider />
                        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                            {/* Превью выбранного файла */}
                            {selectedFile && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            📎 {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSelectedFile(null);
                                                setFilePreview(null);
                                                setMediaCaption('');
                                            }}
                                        >
                                            <ArrowBack />
                                        </IconButton>
                                    </Box>
                                    {filePreview && (
                                        <Box sx={{ mb: 1 }}>
                                            <img
                                                src={filePreview}
                                                alt="Preview"
                                                style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                                            />
                                        </Box>
                                    )}
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Подпись (необязательно)..."
                                        value={mediaCaption}
                                        onChange={(e) => setMediaCaption(e.target.value)}
                                        sx={{ mb: 1 }}
                                    />
                                    <Button
                                        variant="contained"
                                        color="success"
                                        fullWidth
                                        onClick={handleSendMedia}
                                        disabled={sendingMedia}
                                        startIcon={<Send />}
                                    >
                                        {sendingMedia ? 'Отправка...' : 'Отправить файл'}
                                    </Button>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    onChange={handleFileSelect}
                                />
                                <IconButton
                                    color="primary"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={sendingReply || !!selectedFile}
                                >
                                    📎
                                </IconButton>
                                <TextField
                                    fullWidth
                                    multiline
                                    maxRows={4}
                                    placeholder="Введите сообщение..."
                                    value={replyMessage}
                                    onChange={(e) => setReplyMessage(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendReply();
                                        }
                                    }}
                                    disabled={sendingReply || !!selectedFile}
                                    variant="outlined"
                                    size="small"
                                />
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleSendReply}
                                    disabled={!replyMessage.trim() || sendingReply || !!selectedFile}
                                    startIcon={<Send />}
                                    sx={{ minWidth: 100 }}
                                >
                                    {sendingReply ? 'Отправка...' : 'Отправить'}
                                </Button>
                            </Box>
                        </Box>
                    </Card>
                </Box>
            ) : (
                // Список чатов
                <Box>
                    <Typography variant="h4" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WhatsAppIcon sx={{ fontSize: 40, color: '#25d366' }} />
                        WhatsApp
                    </Typography>

                    {/* Статус подключения */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="h6">Статус:</Typography>
                                    {ready && state === 'CONNECTED' ? (
                                        <Chip
                                            icon={<CheckCircle />}
                                            label="Подключен"
                                            color="success"
                                            variant="outlined"
                                        />
                                    ) : state === 'LOGOUT' || state === 'ERROR' ? (
                                        <Chip
                                            icon={<QrCode2 />}
                                            label={`Отключен (${state})`}
                                            color="error"
                                            variant="outlined"
                                        />
                                    ) : (
                                        <Chip
                                            icon={<QrCode2 />}
                                            label={`${state === 'NOT_INITIALIZED' ? 'Инициализация' : state}`}
                                            color="warning"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton onClick={fetchStatus} title="Обновить">
                                        <Refresh />
                                    </IconButton>
                                    {!ready && (state === 'LOGOUT' || state === 'ERROR') && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={async () => {
                                                await apiFetch(`${API_URL}/api/whatsapp/reinit`, { method: 'POST' });
                                                setTimeout(fetchStatus, 2000);
                                            }}
                                        >
                                            Переподключить
                                        </Button>
                                    )}
                                    {ready && (
                                        <>
                                            <Button
                                                variant="contained"
                                                startIcon={<Send />}
                                                onClick={() => setOpenSendDialog(true)}
                                                sx={{ bgcolor: '#25d366', '&:hover': { bgcolor: '#20b858' } }}
                                            >
                                                Отправить сообщение
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={fetchChats}
                                                disabled={loading}
                                            >
                                                Обновить чаты
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </Box>

                            {!ready && qr && (
                                <Box sx={{ mt: 3, textAlign: 'center' }}>
                                    <Alert severity="warning" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>Важно:</strong> Перед сканированием убедитесь, что:
                                        </Typography>
                                        <Typography variant="body2" component="ul" sx={{ textAlign: 'left', mt: 1 }}>
                                            <li>WhatsApp Web не открыт в других браузерах</li>
                                            <li>Нет других активных сессий на компьютере</li>
                                            <li>На телефоне: WhatsApp → Настройки → Связанные устройства</li>
                                        </Typography>
                                    </Alert>
                                    <Typography variant="body1" gutterBottom>
                                        Отсканируйте QR-код с помощью WhatsApp на вашем телефоне:
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <img src={qr} alt="QR для WhatsApp" style={{ maxWidth: 300 }} />
                                    </Box>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Список чатов */}
                    {ready && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Чаты ({chats.length})
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <List>
                                    {chats.length === 0 ? (
                                        <Alert severity="info" sx={{ my: 2 }}>
                                            <Typography variant="body2" gutterBottom>
                                                <strong>Чаты пока не загружены</strong>
                                            </Typography>
                                            <Typography variant="body2">
                                                Baileys загружает чаты автоматически при получении сообщений.
                                                Отправьте или получите сообщение в WhatsApp, и чат появится здесь.
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                Вы можете отправить сообщение самому себе или другому контакту через мобильное приложение.
                                            </Typography>
                                        </Alert>
                                    ) : (
                                        chats.map((chat, index) => (
                                            <ListItem
                                                key={chat.id}
                                                divider={index < chats.length - 1}
                                                onClick={() => handleChatClick(chat)}
                                                sx={{
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: '#25d366' }}>
                                                        <WhatsAppIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body1">
                                                                {chat.name || 'Без имени'}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                {chat.isGroup && (
                                                                    <Chip
                                                                        label="Группа"
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                                {chat.unreadCount > 0 && (
                                                                    <Chip
                                                                        label={chat.unreadCount}
                                                                        size="small"
                                                                        color="error"
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <>
                                                            {chat.lastMessage && (
                                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                                    {chat.lastMessage.body}
                                                                </Typography>
                                                            )}
                                                            {chat.timestamp && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatTimestamp(chat.timestamp)}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    }
                                                />
                                            </ListItem>
                                        ))
                                    )}
                                </List>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            )}

            {/* Диалог отправки сообщения */}
            <Dialog open={openSendDialog} onClose={() => setOpenSendDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Отправить сообщение</DialogTitle>
                <DialogContent>
                    {sendStatus && (
                        <Alert severity={sendStatus.type} sx={{ mb: 2 }}>
                            {sendStatus.message}
                        </Alert>
                    )}
                    <TextField
                        fullWidth
                        label="Номер телефона"
                        placeholder="+79XXXXXXXXX"
                        value={sendData.phone}
                        onChange={(e) => setSendData({ ...sendData, phone: e.target.value })}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        fullWidth
                        label="Сообщение"
                        multiline
                        rows={4}
                        value={sendData.message}
                        onChange={(e) => setSendData({ ...sendData, message: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSendDialog(false)}>Отмена</Button>
                    <Button
                        onClick={handleSendMessage}
                        variant="contained"
                        disabled={!sendData.phone || !sendData.message}
                        sx={{ bgcolor: '#25d366', '&:hover': { bgcolor: '#20b858' } }}
                    >
                        Отправить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Модальное окно для просмотра изображения */}
            <Dialog
                open={openImageDialog}
                onClose={handleCloseImageDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        boxShadow: 'none'
                    }
                }}
            >
                <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
                    {imagePreview && (
                        <img
                            src={imagePreview}
                            alt="Полноразмерное изображение"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                borderRadius: '8px'
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ bgcolor: 'rgba(0, 0, 0, 0.9)' }}>
                    <Button
                        onClick={handleCloseImageDialog}
                        sx={{ color: 'white' }}
                    >
                        Закрыть
                    </Button>
                    <Button
                        onClick={() => window.open(imagePreview, '_blank')}
                        sx={{ color: 'white' }}
                    >
                        Открыть в новой вкладке
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default WhatsAppPanel;
